/* Paperbloom storefront JS: header mega menu, drawers, cart drawer, tabs, sale countdown. No dependencies.
   Public API for other streams:
     document.dispatchEvent(new CustomEvent('pb:cart:open'))     -> re-render cart drawer and open it
     document.dispatchEvent(new CustomEvent('pb:cart:refresh'))  -> re-render only
     window.PB.cart.open() / .refresh() / .add(items)            -> items = [{id, quantity, properties}]
   Impact's own events (variant:add, cart:refresh, cart:change) also refresh the drawer. */
(() => {
  if (window.PB && window.PB.loaded) return;
  const PB = (window.PB = window.PB || {});
  PB.loaded = true;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /* ---------- Dialog helpers ---------- */
  const openDialog = (d) => {
    if (!d || d.open) return;
    d.showModal();
    document.documentElement.classList.add('pb-locked');
  };
  const closeDialog = (d) => {
    if (!d || !d.open) return;
    d.close();
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-pb-open]');
    if (opener) {
      e.preventDefault();
      openDialog(document.getElementById(opener.dataset.pbOpen));
      return;
    }
    const closer = e.target.closest('[data-pb-close]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    // Click on the backdrop (outside the dialog box) closes it.
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('pb-drawer')) {
      const r = e.target.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(e.target);
    }
  });

  /* ---------- Header: mega menu + height var ---------- */
  const header = document.querySelector('[data-pb-header]');
  if (header) {
    const triggers = [...header.querySelectorAll('[data-pb-mega]')];
    const scrim = header.querySelector('[data-pb-scrim]');
    const setOpen = (btn, open) => {
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
      if (scrim) scrim.hidden = !triggers.some((b) => b.getAttribute('aria-expanded') === 'true');
    };
    const closeAll = (except) => triggers.forEach((b) => b !== except && setOpen(b, false));
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    triggers.forEach((btn) => {
      const item = btn.closest('.pb-nav__item');
      let t;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') !== 'true';
        closeAll(btn);
        setOpen(btn, open);
      });
      item.addEventListener('mouseenter', () => {
        if (!canHover.matches) return;
        clearTimeout(t);
        t = setTimeout(() => { closeAll(btn); setOpen(btn, true); }, 90);
      });
      item.addEventListener('mouseleave', () => {
        if (!canHover.matches) return;
        clearTimeout(t);
        t = setTimeout(() => setOpen(btn, false), 180);
      });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) setOpen(btn, false);
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = triggers.find((b) => b.getAttribute('aria-expanded') === 'true');
      if (open) { setOpen(open, false); open.focus(); }
    });
    document.addEventListener('click', (e) => { if (!header.contains(e.target) || e.target === scrim) closeAll(); });

    const setH = () => {
      const h = header.getBoundingClientRect().height;
      document.documentElement.style.setProperty('--header-height', h + 'px');
      document.documentElement.style.setProperty('--sticky-area-height', h + 'px');
    };
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(header);
  }

  /* ---------- Sale countdown ---------- */
  document.querySelectorAll('[data-pb-countdown]').forEach((el) => {
    const end = Date.parse(el.dataset.pbCountdown);
    const out = el.querySelector('[data-pb-countdown-text]') || el;
    if (!end) return;
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let s = Math.floor((end - Date.now()) / 1000);
      if (s <= 0) {
        const bar = el.closest('.pb-announce');
        if (bar) bar.hidden = true;
        clearInterval(iv);
        return;
      }
      const d = Math.floor(s / 86400); s -= d * 86400;
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60); s -= m * 60;
      out.textContent = (d ? d + 'd ' : '') + pad(h) + 'h ' + pad(m) + 'm ' + pad(s) + 's';
    };
    const iv = setInterval(tick, 1000);
    tick();
  });
  // Hide a sale bar whose end passed while the page sat in cache.
  document.querySelectorAll('[data-pb-sale-ends]').forEach((el) => {
    const end = Date.parse(el.dataset.pbSaleEnds);
    if (end && end < Date.now()) el.hidden = true;
  });

  /* ---------- Tabs ([data-pb-tabs] > [role=tab][aria-controls]) ---------- */
  document.querySelectorAll('[data-pb-tabs]').forEach((list) => {
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const select = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const p = document.getElementById(t.getAttribute('aria-controls'));
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
    };
    tabs.forEach((t, i) => {
      t.addEventListener('click', () => select(t));
      t.addEventListener('keydown', (e) => {
        const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (dir) { e.preventDefault(); select(tabs[(i + dir + tabs.length) % tabs.length], true); }
      });
    });
  });

  /* ---------- Live review summary (Stamped badges API; the Shopify metafields lag behind) ---------- */
  document.querySelectorAll('[data-pb-rating]').forEach(async (el) => {
    try {
      const res = await fetch('https://stamped.io/api/widget/badges?isIncludeBreakdown=false&isincludehtml=false', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds: [{ productId: el.dataset.pbRating }], apiKey: '604fdcdf-e098-412d-9502-39a68924c528', storeUrl: 'paperbloomco.myshopify.com' })
      });
      const [b] = await res.json();
      if (!b || !b.count) return;
      const rating = Number(b.rating).toFixed(1);
      el.querySelector('[data-pb-rating-text]').textContent = rating + ' · ' + b.count + (b.count === 1 ? ' review' : ' reviews');
      el.setAttribute('aria-label', 'Rated ' + rating + ' out of 5 from ' + b.count + ' reviews');
      el.hidden = false;
    } catch (e) { /* keep the server-rendered summary */ }
  });

  /* ---------- Plain product page: gallery, qty, roll pills, add to cart ---------- */
  document.querySelectorAll('[data-pb-gallery]').forEach((g) => {
    const main = g.querySelector('.pb-gal__main');
    g.addEventListener('click', (e) => {
      const t = e.target.closest('[data-src]');
      if (!t || !main) return;
      main.removeAttribute('srcset');
      main.src = t.dataset.src;
      g.querySelectorAll('[data-src]').forEach((b) => b.classList.toggle('is-on', b === t));
    });
  });
  const pform = document.getElementById('pb-product-form');
  if (pform) {
    const qty = pform.querySelector('input[name="quantity"]');
    pform.addEventListener('click', (e) => {
      const st = e.target.closest('[data-pb-step]');
      if (st && qty) { qty.value = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(st.dataset.pbStep, 10)); }
    });
    pform.addEventListener('change', (e) => {
      if (e.target.name !== 'id' || !e.target.dataset.price) return;
      const out = pform.querySelector('[data-pb-atc-price]');
      if (out) out.textContent = e.target.dataset.price;
      const price = document.querySelector('[data-pb-price] .pb-price__now');
      if (price) price.textContent = e.target.dataset.price;
    });
    pform.addEventListener('submit', (e) => {
      e.preventDefault();
      const btn = pform.querySelector('[data-pb-atc]');
      const msg = pform.parentElement.querySelector('[data-pb-buy-msg]');
      if (btn) btn.disabled = true;
      fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(pform) })
        .then(async (res) => {
          const json = await safeJson(res);
          if (!res.ok) throw new Error((json && json.description) || 'Could not add to cart');
          document.dispatchEvent(new CustomEvent('pb:cart:open'));
          if (msg) msg.hidden = true;
        })
        .catch((err) => { if (msg) { msg.textContent = err.message; msg.hidden = false; } })
        .finally(() => { if (btn) btn.disabled = false; });
    });
  }

  /* ---------- Recommendations (swap in Shopify's related products when available) ---------- */
  document.querySelectorAll('[data-pb-recs]').forEach(async (sec) => {
    try {
      const res = await fetch(sec.dataset.pbRecs);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const list = doc.querySelector('[data-pb-recs-list]');
      if (list && list.children.length) sec.querySelector('[data-pb-recs-list]').replaceWith(list);
    } catch (e) { /* keep the fallback collection */ }
  });

  /* ---------- Search dialog (predictive, /search/suggest.json) ---------- */
  const sdlg = document.getElementById('pb-search');
  if (sdlg) {
    const input = sdlg.querySelector('[data-pb-search-input]');
    const out = sdlg.querySelector('[data-pb-search-results]');
    const start = sdlg.querySelector('[data-pb-search-start]');
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    const money = (v) => new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(Number(v));
    const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    let timer, ctrl;
    const run = async () => {
      const q = input.value.trim();
      if (ctrl) ctrl.abort();
      if (q.length < 2) { out.innerHTML = ''; if (start) start.hidden = false; return; }
      ctrl = new AbortController();
      try {
        const url = root + 'search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product,collection,query&resources[limit]=8&resources[options][unavailable_products]=hide&resources[options][fields]=title,tag,product_type,variants.title,body';
        const data = await safeJson(await fetch(url, { signal: ctrl.signal }));
        const r = (data && data.resources && data.resources.results) || {};
        const prods = (r.products || []).filter((p) => !/wallpaper/i.test(p.type || '') && !(p.tags || []).includes('needs-rebuild'));
        const cols = r.collections || [];
        const ideas = (r.queries || []).slice(0, 5);
        let html = '';
        // What other shoppers search that starts the same way: one tap to the full results.
        if (ideas.length) html += '<p class="pb-search__cols">' + ideas.map((s) => '<a class="pb-chip-btn" href="' + esc(s.url) + '&type=product">' + esc(s.text) + '</a>').join('') + '</p>';
        if (prods.length) {
          html += '<ul class="pb-search__prods" role="list">' + prods.map((p) =>
            '<li><a href="' + esc(p.url) + '"><img src="' + esc((p.featured_image && p.featured_image.url || p.image || '') + (String(p.image || '').includes('?') ? '&' : '?') + 'width=160') + '" alt="" width="80" height="80" loading="lazy"><span><b>' + esc(p.title) + '</b><small>' + (p.price_max !== p.price_min || p.price_max !== p.price ? 'From ' : '') + money(p.price_min || p.price) + '</small></span></a></li>').join('') + '</ul>';
        }
        if (cols.length) html += '<p class="pb-search__cols">' + cols.map((c) => '<a class="pb-chip-btn" href="' + esc(c.url) + '">' + esc(c.title) + '</a>').join('') + '</p>';
        if (prods.length || cols.length) html += '<a class="pb-more" href="' + root + 'search?q=' + encodeURIComponent(q) + '&type=product&options[prefix]=last">See all results for “' + esc(q) + '”</a>';
        else html = '<p class="pb-search__none">Nothing for “' + esc(q) + '” yet. Try a shorter word, or start from one of these.</p>';
        out.innerHTML = html;
        // No matches: the starting points come back instead of an empty box.
        if (start) start.hidden = !!(prods.length || cols.length);
      } catch (e) { /* aborted or offline */ }
    };
    input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 180); });
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-pb-open="pb-search"]')) setTimeout(() => input.focus(), 30);
    });
  }

  /* ---------- "Chat with us": open Gorgias when it's on the page, else follow the contact link ---------- */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('[data-pb-chat]');
    if (!a || !window.GorgiasChat) return;
    e.preventDefault();
    const go = () => { try { window.GorgiasChat.open(); } catch (err) { window.location.href = a.href; } };
    if (typeof window.GorgiasChat.init === 'function') window.GorgiasChat.init().then(go).catch(go); else go();
  });

  /* ---------- Cookie bar + "Your privacy choices" (Shopify Customer Privacy API) ----------
     Shown only when Shopify says this visitor must be asked (EU/UK etc.), and never when Shopify's own
     cookie banner is enabled in the admin (it renders #shopify-pc__banner). Where US state privacy laws
     apply (saleOfDataRegion), a footer link lets the visitor opt out of the sale/sharing of their data. */
  const pbar = document.querySelector('[data-pb-privacy]');
  const choices = document.querySelector('[data-pb-privacy-choices]');
  if ((pbar || choices) && window.Shopify && Shopify.loadFeatures) {
    Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1', onLoad: (err) => {
      const cp = window.Shopify.customerPrivacy;
      if (err || !cp) return;
      setTimeout(() => {
        const native = document.getElementById('shopify-pc__banner');
        if (pbar && !native && cp.shouldShowBanner && cp.shouldShowBanner()) pbar.hidden = false;
        if (choices && cp.saleOfDataRegion && cp.saleOfDataRegion()) choices.hidden = false;
      }, 600);
    } }]);
    const setConsent = (all, done) => {
      const cp = window.Shopify.customerPrivacy;
      if (!cp) return;
      cp.setTrackingConsent({ analytics: all, marketing: all, preferences: all, sale_of_data: all }, done);
    };
    if (pbar) pbar.addEventListener('click', (e) => {
      const b = e.target.closest('[data-pb-consent]');
      if (b) setConsent(b.dataset.pbConsent === 'true', () => { pbar.hidden = true; });
    });
    if (choices) choices.addEventListener('click', (e) => {
      e.preventDefault();
      const cp = window.Shopify.customerPrivacy;
      if (!cp) return;
      cp.setTrackingConsent({ sale_of_data: false }, () => { choices.textContent = 'Opted out of data sale/sharing'; });
    });
  }

  /* ---------- Auto-submit (collection sort) ---------- */
  document.addEventListener('change', (e) => {
    const el = e.target.closest('[data-pb-autosubmit]');
    if (el && el.form) el.form.requestSubmit ? el.form.requestSubmit() : el.form.submit();
  });

  /* ---------- Cart drawer ---------- */
  const drawer = () => document.getElementById('pb-cart');
  let busy = null;

  const setCount = (count) => {
    document.querySelectorAll('[data-pb-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });
    document.querySelectorAll('[data-pb-cart-label]').forEach((el) =>
      el.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items')));
  };

  const refresh = async () => {
    const d = drawer();
    if (!d) return;
    const res = await fetch(root + '?section_id=' + encodeURIComponent(d.dataset.sectionId), { credentials: 'same-origin' });
    if (!res.ok) return;
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.getElementById('pb-cart');
    if (!fresh) return;
    const body = d.querySelector('.pb-cart__body');
    const scroll = body ? body.scrollTop : 0;
    d.replaceChildren(...fresh.childNodes);
    const nb = d.querySelector('.pb-cart__body');
    if (nb) nb.scrollTop = scroll;
    setCount(parseInt(fresh.dataset.count || '0', 10));
    // Shopify re-initialises dynamic checkout buttons on this hook when present.
    if (window.Shopify && Shopify.PaymentButton && Shopify.PaymentButton.init) Shopify.PaymentButton.init();
  };

  const open = async () => {
    const d = drawer();
    if (!d) { window.location.href = root + 'cart'; return; }
    openDialog(d);
    await refresh();
  };

  // Never let an HTML error page (bot check, 5xx) surface as an unhandled JSON parse error.
  const safeJson = async (res) => {
    const text = await res.text();
    try { return JSON.parse(text); } catch { return null; }
  };

  const post = async (url, body) => {
    const res = await fetch(root + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const json = await safeJson(res);
    if (!res.ok || !json) throw new Error((json && (json.description || json.message)) || 'Something went wrong. Please try again.');
    return json;
  };

  const withBusy = async (fn) => {
    const d = drawer();
    const body = d && d.querySelector('.pb-cart__body');
    if (body) body.setAttribute('aria-busy', 'true');
    try {
      await fn();
    } catch (err) {
      const box = d && d.querySelector('[data-pb-cart-error]');
      if (box) { box.textContent = err.message; box.hidden = false; }
    } finally {
      if (body) body.removeAttribute('aria-busy');
    }
  };

  const notify = (cart) => {
    // Tell Impact (and anyone else) the cart changed; baseEvent lets us ignore our own echo.
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'pb', cart } }));
  };

  const change = (line, quantity) => withBusy(async () => {
    const cart = await post('cart/change.js', { line, quantity });
    if (document.querySelector('[data-pb-cart-page]')) { window.location.reload(); return; }
    await refresh();
    notify(cart);
  });

  const add = (items) => withBusy(async () => {
    await post('cart/add.js', { items });
    const cart = await safeJson(await fetch(root + 'cart.js'));
    await refresh();
    openDialog(drawer());
    notify(cart);
  });

  PB.cart = { open, refresh, add, change };

  // Capture phase: some third-party scripts stop click propagation before it reaches document.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-pb-cart-open]');
    if (!trigger || !drawer() || document.body.classList.contains('template-cart')) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    const menu = trigger.closest('dialog');
    if (menu && menu.id !== 'pb-cart') closeDialog(menu);
    quiet(open)();
  }, true);

  document.addEventListener('click', (e) => {
    const qty = e.target.closest('[data-pb-qty]');
    if (qty) {
      e.preventDefault();
      change(parseInt(qty.dataset.line, 10), Math.max(0, parseInt(qty.dataset.pbQty, 10)));
      return;
    }
    const up = e.target.closest('[data-pb-upsell]');
    if (up) {
      e.preventDefault();
      up.disabled = true;
      add([{ id: parseInt(up.dataset.pbUpsell, 10), quantity: 1 }]);
    }
  }, true);

  const quiet = (fn) => (...a) => Promise.resolve().then(() => fn(...a)).catch((err) => console.warn('[pb cart]', err));
  document.addEventListener('pb:cart:open', quiet(open));
  document.addEventListener('pb:cart:refresh', quiet(refresh));
  document.addEventListener('cart:refresh', quiet(refresh));
  document.addEventListener('variant:add', quiet(open));
  document.addEventListener('cart:change', (e) => {
    if (e.detail && e.detail.baseEvent === 'pb') return;
    quiet(refresh)();
  });
  window.addEventListener('pageshow', (e) => { if (e.persisted) quiet(refresh)(); });
})();

/* Print file. The print room's software reads the order's print link and can only decode a JPEG, but the studio
   draws a see-through PNG. So a line whose print link is a studio PNG gets the address of its JPEG instead (the relay
   makes it at /api/print-file: same pixels, 300 dpi, like Customily's files), and keeps the PNG in "_print-source".
   The JPEG's address comes from the design's id, so this never waits; the relay's production sweep makes any file
   this call missed. Runs for every personalized line via PBPreviewTag below. */
(function () {
  const RELAY = 'https://pb-studio-relay.vercel.app/api/print-file';
  const PNG = /^https:\/\/[a-z0-9-]+\.modal\.run\/files\/exports\/production\/customily-id-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.png$/;
  window.PBPrintFile = function (props) {
    try {
      const url = props && props['_customily-production-url'];
      const m = PNG.exec(url || '');
      if (!m) return;
      props['_print-source'] = url;
      props['_customily-production-url'] = 'https://lhkttcdbbvammypbgkus.supabase.co/storage/v1/object/public/designs/print-' + m[1] + '.jpg';
      fetch(RELAY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }), keepalive: true }).catch(() => {});
    } catch (e) { /* the sweep makes the file */ }
  };
})();

/* Checkout picture. Shopify's checkout shows the product's stock photo; the pb-cart-preview app (a cart transform
   function) swaps in the shopper's own design. For that, each personalized line carries the visible property
   "Design" (a short code taken from its preview file's name: functions can't read "_" properties), and the relay
   copies the preview onto Shopify's CDN as pbprev-<code>.jpg (pb-studio-relay /api/preview-file). Called with a
   line's properties just before it is added to the cart; never blocks or fails the add. */
(function () {
  const RELAY = 'https://pb-studio-relay.vercel.app/api/preview-file';
  // Same list as the relay's (api/preview-file.js OK_SOURCE): a Customily-era design uses its print file (the flat
  // design), because Customily's previews are a mockup.
  const OK = /^https:\/\/([a-z0-9-]+\.modal\.run\/files\/exports\/(checkout|production)\/|lhkttcdbbvammypbgkus\.supabase\.co\/storage\/v1\/object\/public\/designs\/|cdn\.customily\.com\/ExportFile\/paperbloomco\/)[A-Za-z0-9._-]+$/;
  window.PBPreviewTag = function (props) {
    window.PBPrintFile(props);
    try {
      const pv = props && (props['_customily-preview'] || props['_customily-thumb'] || props['_Design-URL']);
      const url = OK.test(pv || '') ? pv : OK.test((props && props['_customily-production-url']) || '') ? props['_customily-production-url'] : '';
      if (!url) return;
      const code = url.split('/').pop().replace(/\.[a-z]+$/i, '').replace(/-preview$/, '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(-12);
      if (code.length < 8) return;
      props['Design'] = code;
      fetch(RELAY, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code, url }), keepalive: true }).catch(() => {});
    } catch (e) { /* the picture is a nicety */ }
  };

  /* A personalized line already in the cart without its "Design" code (added before this existed, or by a path that
     skipped PBPreviewTag) shows the stock photo at checkout. Once per page, any such line is given its code (and its
     picture is made); the rest of the line stays exactly as it was. */
  async function heal() {
    try {
      const base = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
      const cart = await (await fetch(base + 'cart.js', { credentials: 'same-origin' })).json();
      let changed = false;
      for (const [i, item] of (cart.items || []).entries()) {
        const props = Object.assign({}, item.properties || {});
        if (props['Design'] || !(props['_customily-preview'] || props['_Design-URL'] || props['_customily-production-url'])) continue;
        window.PBPreviewTag(props);
        if (!props['Design']) continue;
        const r = await fetch(base + 'cart/change.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: i + 1, quantity: item.quantity, properties: props }) });
        if (r.ok) changed = true;
      }
      if (changed && window.PB && window.PB.cart && typeof window.PB.cart.refresh === 'function') window.PB.cart.refresh();
    } catch (e) { /* the picture is a nicety */ }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(heal, 400));
  else setTimeout(heal, 400);
})();

/* Holiday order deadline: a note above Add to cart (product pages, the customizer, Face It, the AI studio's
   last step) and in the cart drawer, counting the days left. Everything disappears once the date passes. The
   date is the announcement section's "Last order date" when its banner is showing, else DEADLINE below. */
(function () {
  const DEADLINE = '2026-12-15';
  const day = (document.querySelector('[data-pb-deadline-ann]')?.getAttribute('data-pb-deadline-ann') || DEADLINE).trim();
  const end = new Date(`${day}T23:59:59-07:00`); // Arizona time, end of that day
  if (Number.isNaN(end.getTime())) return;
  const label = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Phoenix' });
  const daysLeft = () => Math.ceil((end - Date.now()) / 86400000);
  const ICON = '<svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="5" width="17" height="15" rx="2"/><path d="M3.5 10h17M8 3v4M16 3v4"/></svg>';

  function text(n) {
    const final = n <= 7;
    const left = n === 1 ? 'Today is the last day' : `${n} days left`;
    return {
      final,
      title: final ? (n === 1 ? 'Last day to order for the holidays' : `Only ${n} days left to order for the holidays`) : `Order by ${label} for holiday delivery`,
      sub: final ? `Order by ${label} so it arrives in time. Every roll is printed to order in the USA.` : `${left}. Every roll is printed to order in the USA, so order by ${label} to get it in time.`,
      line: final ? `Order by ${label} (${n === 1 ? 'today is the last day' : `${n} days left`}) to get it in time for the holidays.` : `Order by ${label} to get it in time for the holidays.`,
    };
  }

  function box(kind, t) {
    const b = document.createElement('div');
    b.className = `pb-deadline pb-deadline--${kind}${t.final ? ' is-final' : ''}`;
    b.setAttribute('data-pb-deadline', '');
    b.setAttribute('role', 'note');
    if (kind === 'line') b.textContent = t.line;
    else b.innerHTML = `${ICON}<div><strong></strong><span></span></div>`, b.querySelector('strong').textContent = t.title, b.querySelector('span').textContent = t.sub;
    return b;
  }

  // Where it goes: before each of these (once). The AI studio's button only counts on its Add to cart step.
  const SPOTS = [
    ['.pbcz__buy', 'full'],
    ['form .pb-buy', 'full'],
    ['.pb-cart__f [name="checkout"]', 'line'],
  ];
  function place() {
    const n = daysLeft();
    if (n < 1) {
      document.querySelectorAll('[data-pb-deadline], [data-pb-deadline-ann]').forEach((x) => x.remove());
      return false;
    }
    const t = text(n);
    for (const [sel, kind] of SPOTS) {
      document.querySelectorAll(sel).forEach((at) => {
        const prev = at.previousElementSibling;
        if (prev && prev.hasAttribute('data-pb-deadline')) return;
        at.before(box(kind, t));
      });
    }
    const ai = document.querySelector('[data-pb-ai-next]');
    if (ai) {
      const row = ai.parentElement;
      const onCartStep = /^Add(ed)? to cart/.test(ai.textContent.trim());
      const have = row.previousElementSibling?.hasAttribute('data-pb-deadline');
      if (onCartStep && !have) row.before(box('full', t));
      else if (!onCartStep && have) row.previousElementSibling.remove();
    }
    return true;
  }

  let queued = false;
  const run = () => { queued = false; if (!place()) observer.disconnect(); };
  const observer = new MutationObserver(() => { if (!queued) { queued = true; requestAnimationFrame(run); } });
  const start = () => { if (place()) observer.observe(document.body, { childList: true, subtree: true, characterData: true }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
